
import { Component, Vue } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { emptyToLine, splitThousands, getDateRange } from "@common/src/utils/common";
import dayjs from "dayjs";
import BtnRadio from "@common/src/components/scope/btn-radio/index.vue";
import echarts from "echarts";
import appCode from "@common/src/utils/app-code";
import t from "@common/src/i18n/t";
import { addCurrencyUnit } from "@common/src/utils/tools";
import TextTips from "@common/src/components/scope/text-tips/index.vue";
import { InjectFunction } from "@common/src/utils/decorator";

const statement = namespace("statement");
const base = namespace("base");
const management = namespace("management");
const serviceItem = namespace("service-item");
const parts = namespace("parts");
const common_statement = namespace("common_statement");

@InjectFunction("permitApp")
@Component({
  components: { BtnRadio, TextTips },
  filters: {}
})
export default class Statement extends Vue {
  @statement.Action getTurnOverAnalysisModel;
  @base.Action getDictionaryList;
  @management.Action getStoreList;
  @serviceItem.Action getServiceItemList;
  @parts.Action getPartsList;
  @common_statement.State storeSearchForm;
  @common_statement.State storeSearchList;
  addCurrencyUnit = addCurrencyUnit;
  dayjs = dayjs;
  splitThousands = splitThousands;
  emptyToLine = emptyToLine;
  searchForm: any = {
    storeCode: "", // 门店名称
    area: "", // 所在地区
    stateName: "", // 省级名称
    cityName: "", // 市级名称
    districtName: "", // 县级名称
    timeFrameType: "date", // 时间范围类型, date-按日,month-按月
    startTime: "", // 开始时间 格式: 按日为yyyyMMdd, 按月为yyyyMM
    endTime: "", // 结束时间 格式: 按日为yyyyMMdd, 按月为yyyyMM
    businessType: "services", // 服务类型，services-服务项目,parts-零配件
    businessCodeList: [] // 服务编码列表
  };
  pieChartRadio: any = "0";
  barChartRadio: any = "0";
  routeParams: any = {};
  created() {
    this.init();
  }
  breadData = [
    { name: t("v210831.turnover-statement"), path: "/statement/turnover-list" },
    { name: t("v210831.turnover-analysis") }
  ];
  storeList: any = [];
  serviceList: any = [];
  partsList: any = [];
  get searchList() {
    return [
      {
        label: t("v216_6.statistical-dimension"),
        type: "btn-radio",
        prop: "timeFrameType",
        options: this.dateTypeOptions,
        value: this.searchForm.timeFrameType,
        typeChangeMethod: this.handleDateTypeChange,
        required: true
      },
      ...(this.searchForm.timeFrameType === "date"
        ? [
            {
              label: t("v210831.time-frame"),
              value: [this.searchForm.startTime, this.searchForm.endTime],
              resetValue: this.getDefaultDate("date"),
              type: "daterange",
              prop: "date",
              required: true,
              disabledFuture: true,
              maxDay: 90
            }
          ]
        : [
            {
              label: t("v210831.time-frame"),
              value: [this.searchForm.startTime, this.searchForm.endTime],
              resetValue: this.getDefaultDate("month"),
              type: "monthrange",
              prop: "month",
              required: true,
              disabledFuture: true,
              maxMonth: 12
            }
          ]),
      {
        label: t("service.project-spare-parts"),
        type: "btn-radio",
        prop: "businessType",
        options: this.itemTypeOptions,
        value: this.searchForm.businessType,
        typeChangeMethod: this.handleItemTypeChange,
        required: true
      },
      ...(this.searchForm.businessType === "services"
        ? [
            {
              label: t("v213.Service"),
              type: "select-search",
              prop: "businessCodeList1",
              valueAlias: "businessCodeList",
              value: this.searchForm.businessCodeList,
              selectList: this.serviceList,
              searchFun: this.searchService,
              multiple: true
            }
          ]
        : [
            {
              label: t("parts.text"),
              type: "select-search",
              prop: "businessCodeList2",
              valueAlias: "businessCodeList",
              value: this.searchForm.businessCodeList,
              selectList: this.partsList,
              searchFun: this.searchParts,
              multiple: true
            }
          ]),
      ...(appCode === "speedbiz-operation"
        ? [
            {
              label: t("staff.store"),
              type: "select-search",
              value: this.searchForm.storeCode,
              prop: "storeCode",
              selectList: this.storeList,
              searchFun: this.searchStore,
              clearable: false
            },
            {
              label: t("setting.region"),
              type: "area",
              value: this.searchForm.area,
              labels: [],
              prop: "area",
              anyLevel: true,
              valueDistribute: ["stateName", "cityName", "districtName"]
            }
          ]
        : [])
    ];
  }
  searchStore(val = "") {
    this.getStoreList({
      pageNum: 1,
      pageSize: 10,
      ...(this.searchForm.fromList ? { storeCode: this.routeParams.storeCode || "" } : { storeName: val })
    }).then(data => {
      this.storeList = data.data.list
        ? data.data.list.map(item => {
            item.label = item.storeName;
            item.value = item.storeCode;
            return item;
          })
        : [];
    });
  }
  searchService(val = "") {
    this.getServiceItemList({
      status: "1",
      pageNum: 1,
      pageSize: 10,
      ...(this.searchForm.fromList ? { serviceCodeList: this.routeParams.businessCodeList || [] } : { searchKey: val })
    }).then(data => {
      this.serviceList = data.data.list
        ? data.data.list.map(item => {
            item.label = item.serviceName;
            item.value = item.serviceCode;
            return item;
          })
        : [];
    });
  }
  searchParts(val = "") {
    this.getPartsList({
      status: "1",
      pageNum: 1,
      pageSize: 10,
      ...(this.searchForm.fromList ? { partsCodeList: this.routeParams.businessCodeList || [] } : { partsName: val })
    }).then(data => {
      this.partsList = this.$safeValue(data, "data.list", []).map(item => {
        item.label = item.partsName;
        item.value = item.partsCode;
        return item;
      });
    });
  }
  init() {
    this.routeParams = this.$route.params;
    if (this.routeParams.fromList === "1") {
      this.searchForm = this.routeParams;
    } else {
      this.handleDateTypeChange(this.searchForm.timeFrameType);
    }
    this.searchService();
    this.searchParts();
    if (appCode === "speedbiz-operation") {
      this.searchStore();
    }
    delete this.searchForm.fromList;
    this.getChartData();
  }
  dataLastUpdateTime: any = "";
  chartServerData: any = {};
  getChartData() {
    this.getTurnOverAnalysisModel(this.searchForm).then(data => {
      if (data.data.dataLastUpdateTime) {
        this.dataLastUpdateTime = data.data.dataLastUpdateTime;
      }

      let { orderNumberStatistics, orderNumberMonthlyList, orderCollectionMonthlyList } = data.data;
      this.chartServerData = data.data;
      this.handlePieChange("0");
      this.handleBarChange("0");
      /**
       * @refName
       *  饼图 实收金额   -----payAmountStatistics
       *  饼图 订单金额   -----orderAmountStatistics
       *  pieNumberChart  饼图 台次   -----orderNumberStatistics
       *  柱状图 实收金额   -----payAmountMonthlyList
       *  柱状图 订单金额   -----orderAmountMonthlyList
       *  barNumberChart  月度开单类型台次统计   -----orderNumberMonthlyList
       *  lineChart  折线图   -----orderCollectionMonthlyList
       */
      let pieNumberChartData = {
        data: [
          {
            name: t("dashboard.repair-maintenance"),
            value: orderNumberStatistics.maintenanceNumber
          },
          {
            name: t("dashboard.wash-order"),
            value: orderNumberStatistics.carWashNumber
          },
          {
            name: t("dashboard.retail-order"),
            value: orderNumberStatistics.retailNumber
          }
        ],
        total: orderNumberStatistics.totalNumber,
        text: t("v210831.summary-of-sets"),
        tip: t("v210831.number-of-sets"),
        isNumber: true
      };
      this.setChart(pieNumberChartData, "pieNumberChart", "pie");

      let barNumberChartData = {
        data1: orderNumberMonthlyList.map(v => v.maintenanceNumber),
        data2: orderNumberMonthlyList.map(v => v.carWashNumber),
        data3: orderNumberMonthlyList.map(v => v.retailNumber),
        originData: orderNumberMonthlyList,
        unit: t("v210831.number-of-sets"),
        isNumber: true
      };
      this.setChart(barNumberChartData, "barNumberChart", "bar");
      let lineChartData = {
        data1: orderCollectionMonthlyList.map(v => (v.orderAmount ? Number(v.orderAmount) : null)),
        data2: orderCollectionMonthlyList.map(v => (v.payAmount ? Number(v.payAmount) : null)),
        data3: orderCollectionMonthlyList.map(v => (v.onAccountAmount ? Number(v.onAccountAmount) : 0)),
        data4: orderCollectionMonthlyList.map(v => (v.retailDiscountAmount ? Number(v.retailDiscountAmount) : 0)),
        originData: orderCollectionMonthlyList,
        unit: this.$localMark.currency
      };
      this.setChart(lineChartData, "lineChart", "line");
    });
  }
  setChart(data, refName, type) {
    let xAxisData = [];
    if (data.originData) {
      data.originData.forEach(item => {
        xAxisData.push(item.monthParam);
      });
      // let firstMonth = Number(data.originData[0].monthParam.split("-")[1]);
      // xAxisData = xAllArr.slice(firstMonth - 1, firstMonth - 1 + data.originData.length);
    }
    let MyChart = echarts.init(this.$refs[refName]);
    let options = {};
    if (type === "pie") {
      // 饼图pie
      options = {
        color: ["#2278F5", "#00B478", "#FFC300"],
        tooltip: {
          trigger: "item",
          formatter: params => {
            return `${params.name}<br/>${data.tip}: ${
              data.isNumber ? params.value : this.$localMark.currencySymbol + splitThousands(params.value)
            }(${params.percent}%)`;
          }
        },
        legend: {
          bottom: "0%",
          left: "center",
          x: "center",
          itemWidth: 12,
          itemHeight: 12,
          itemGap: 24
        },
        title: {
          show: true,
          text: data.text,
          subtext: splitThousands(data.total),
          top: "43%",
          left: "center",
          textStyle: {
            color: "rgba(0, 0, 0, 0.45)",
            fontWeight: 400,
            fontSize: 12
          },
          subtextStyle: {
            color: "rgba(0, 0, 0, 0.85)",
            fontWeight: 500,
            fontSize: 18
          }
        },
        series: [
          {
            name: "",
            type: "pie",
            radius: ["40%", "70%"],
            avoidLabelOverlap: false,
            // minAngle: 5,
            label: {
              show: false
            },
            data: data.data
          }
        ]
      };
    } else if (type === "bar") {
      // 柱状图bar
      options = {
        color: ["#2278F5", "#00B478", "#FFC300"],
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow"
          },
          formatter: params => {
            let str = `${data.originData[params[0].dataIndex].monthParam}<br/>`;
            params.forEach(item => {
              str += `${item.marker}${item.seriesName}: ${
                data.isNumber ? item.value : this.$localMark.currencySymbol + splitThousands(item.value)
              }<br/>`;
            });
            return str;
          }
        },
        legend: {
          data: [t("dashboard.repair-maintenance"), t("dashboard.wash-order"), t("dashboard.retail-order")],
          bottom: "0%",
          left: "center",
          x: "center",
          itemWidth: 12,
          itemHeight: 12,
          itemGap: 24
        },
        xAxis: [
          {
            type: "category",
            axisTick: {
              alignWithLabel: true
            },
            data: xAxisData
          }
        ],
        yAxis: [
          {
            name: t("base.unit") + ":" + data.unit,
            axisTick: { show: false },
            type: "value"
          }
        ],
        series: [
          {
            name: t("dashboard.repair-maintenance"),
            type: "bar",
            barGap: 0,
            data: data.data1,
            barWidth: data.data1.length < 5 ? 40 : null
          },
          {
            name: t("dashboard.wash-order"),
            type: "bar",
            data: data.data2,
            barWidth: data.data2.length < 5 ? 40 : null
          },
          {
            name: t("dashboard.retail-order"),
            type: "bar",
            data: data.data3,
            barWidth: data.data3.length < 5 ? 40 : null
          }
        ]
      };
    } else if (type === "line") {
      // 折线图line
      options = {
        color: ["#2278F5", "#00B478", "#FFC300", "#f74e3b"],
        tooltip: {
          trigger: "axis",
          formatter: params => {
            let str = `${data.originData[params[0].dataIndex].monthParam}<br/>`;
            params.forEach(item => {
              str += `${item.marker}${item.seriesName}: ${
                this.$localMark.currencySymbol + " " + splitThousands(item.value)
              }<br/>`;
            });
            return str;
          }
        },
        legend: {
          data: [t("v213.Order"), t("v210831.paid-in-amount"), t("v213.On-account"), t("v213.Discount")],
          bottom: "0%",
          left: "center",
          x: "center",
          itemGap: 24
        },
        xAxis: [
          {
            type: "category",
            axisTick: {
              alignWithLabel: true
            },
            data: xAxisData
          }
        ],
        yAxis: [
          {
            name: t("base.unit") + ":" + data.unit,
            axisTick: { show: false },
            type: "value"
          }
        ],
        series: [
          {
            name: t("v213.Order"),
            type: "line",
            barGap: 0,
            data: data.data1
          },
          {
            name: t("v210831.paid-in-amount"),
            type: "line",
            data: data.data2
          },
          {
            name: t("v213.On-account"),
            type: "line",
            data: data.data3
          },
          {
            name: t("v213.Discount"),
            type: "line",
            data: data.data4
          }
        ]
      };
    }
    MyChart.setOption(options);
    if (type === "pie") {
      const getSelectedTotal = selected => {
        return data.data.reduce((total, item) => {
          if (selected && selected[item.name] === false) {
            return total;
          }
          const value = Number(item.value || 0);
          return total + (isNaN(value) ? 0 : value);
        }, 0);
      };
      MyChart.off("legendselectchanged");
      MyChart.on("legendselectchanged", params => {
        MyChart.setOption({
          title: {
            subtext: splitThousands(getSelectedTotal(params.selected))
          }
        });
      });
    }
    window.addEventListener("resize", () => {
      MyChart.resize();
    });
  }
  handlePieChange(value) {
    this.pieChartRadio = value;
    const refName = "orderTypePieChart";
    const tabTypeDataMap = {
      "0": "payAmountStatistics",
      "1": "orderAmountStatistics",
      "2": "onAccountAmountStatistics",
      "3": "discountAmountStatistics"
    };
    const textDataMap = {
      "0": {
        text: addCurrencyUnit(t("v210831.total-paid-in"), false, true),
        tip: t("v210831.paid-in-amount")
      },
      "1": {
        text: addCurrencyUnit(t("v210831.total-order-amount"), false, true),
        tip: t("v213.Order")
      },
      "2": {
        text: addCurrencyUnit(t("v213.On-account"), false, true),
        tip: t("v213.On-account")
      },
      "3": {
        text: addCurrencyUnit(t("v213.Discount"), false, true),
        tip: t("v213.Discount")
      }
    };
    const chartData = {
      data: [
        {
          name: t("dashboard.repair-maintenance"),
          value: this.chartServerData[tabTypeDataMap[value]].maintenanceAmount
        },
        {
          name: t("dashboard.wash-order"),
          value: this.chartServerData[tabTypeDataMap[value]].carWashAmount
        },
        {
          name: t("dashboard.retail-order"),
          value: this.chartServerData[tabTypeDataMap[value]].retailAmount
        }
      ],

      total: this.chartServerData[tabTypeDataMap[value]].totalAmount,
      ...textDataMap[value]
    };
    this.setChart(chartData, refName, "pie");
  }
  handleBarChange(value) {
    this.barChartRadio = value;
    const refName = "monthlyOrderTypeBarChart";
    const tabTypeDataMap = {
      "0": "payAmountMonthlyList",
      "1": "orderAmountMonthlyList",
      "2": "onAccountAmountMonthlyList",
      "3": "discountAmountMonthlyList"
    };
    const textDataMap = {
      "0": {
        originData: this.chartServerData[tabTypeDataMap[value]],
        unit: this.$localMark.currency
      },
      "1": {
        originData: this.chartServerData[tabTypeDataMap[value]],
        unit: this.$localMark.currency
      },
      "2": {
        originData: this.chartServerData[tabTypeDataMap[value]],
        unit: this.$localMark.currency
      },
      "3": {
        originData: this.chartServerData[tabTypeDataMap[value]],
        unit: this.$localMark.currency
      }
    };
    const chartData = {
      data1: this.chartServerData[tabTypeDataMap[value]].map(v => v.maintenanceAmount),
      data2: this.chartServerData[tabTypeDataMap[value]].map(v => v.carWashAmount),
      data3: this.chartServerData[tabTypeDataMap[value]].map(v => v.retailAmount),
      total: this.chartServerData[tabTypeDataMap[value]].totalAmount,
      ...textDataMap[value]
    };
    this.setChart(chartData, refName, "bar");
  }
  handleSearch(data) {
    this.mix_pageNum = 1;
    data.forEach(item => {
      if (item.prop) {
        if (item.prop === "area") {
          this.searchForm.stateName = item.labels[0] || "";
          this.searchForm.cityName = item.labels[1] || "";
          this.searchForm.districtName = item.labels[2] || "";
        } else if (item.prop === "date" || item.prop === "month") {
          this.searchForm.startTime = item.value?.[0];
          this.searchForm.endTime = item.value?.[1];
        } else {
          this.searchForm[item.valueAlias || item.prop] = item.value;
        }
      }
    });
    if (this.permitApp("speedbiz-operation") && !this.searchForm.storeCode) {
      return this.$message.error(t("v210831.please-select-a-store"));
    }
    this.getChartData();
  }
  dateTypeOptions = [
    { label: t("sb_copy.query-by-day"), value: "date" },
    { label: t("sb_copy.query-by-month"), value: "month" }
  ];
  itemTypeOptions = [
    { label: t("v213.Service"), value: "services" },
    { label: t("parts.text"), value: "parts" }
  ];
  handleDateTypeChange(val) {
    [this.searchForm.startTime, this.searchForm.endTime] = this.getDefaultDate(val);
    this.searchForm.timeFrameType = val;
  }
  handleItemTypeChange(val) {
    this.searchForm.businessCodeList = [];
    this.searchForm.businessType = val;
  }
  getDefaultDate(type: "date" | "month"): Array<string> {
    if (type === "date") {
      return getDateRange("today_to_today", "YYYYMMDD");
    } else if (type === "month") {
      return getDateRange("current_month_to_current_month", "YYYYMM");
    } else {
      return [];
    }
  }
}
